/**
 * toNotificationRecord — the gw-spa adapter from a decoded wire event to
 * gw-ui's `NotificationRecord` (PRP-PLAT-026 FR-2 / GW-8885, spec §3.3:
 * `gw-ui/docs/design/notifications-display-spec.md`).
 *
 * This is the seam that keeps `@gw/ui`'s types independent of
 * `@gw-notifications/appsync-events-client` — the client package stays a
 * gw-spa-only dependency (spec §1.1/§3.3).
 *
 * Field-by-field mapping (spec §3.3):
 *   - `id`:        the wire event's server-minted `id` field (GW-8880 — the
 *                   gw-notifications dispatcher appends the EventBridge
 *                   envelope's `event_id`). NEVER a re-derived composite —
 *                   spec §3.1 explicitly prohibits that (multiple
 *                   `phase_changed` events for the same run are distinct
 *                   notifications, so `type`+`emittedAt` would collide).
 *   - `type`:       wire `type`, verbatim.
 *   - `emittedAt`:  wire `emittedAt`, verbatim.
 *   - `read`:       always `false` — a freshly-decoded wire event has never
 *                   been read; gw-ui never auto-marks read on render, and
 *                   read/unread bookkeeping from here on is host-owned
 *                   (the zustand slice, GW-8886).
 *   - `severity`/`title`/`description`:
 *       known type   -> `getNotificationDisplay(type, payload)` (@gw/ui) —
 *                        the single copy table shared with gw-ui's own
 *                        Storybook fixtures, so toast and feed row can
 *                        never diverge.
 *       unknown type with wire `title` + a recognized `severity` -> used
 *                        verbatim.
 *       unknown type without                                    -> severity
 *                        `'info'`, title = the wire `type` string itself.
 *   - `context`:    known type -> the mapped `NotificationContext` kind
 *                   (today only the shipped `mission.run.*` family exists,
 *                   so this is `'mission-run'`; the other four kinds listed
 *                   in FR-3 have no shipped wire contract yet and gain a
 *                   branch here alongside their `getNotificationDisplay`
 *                   entry when their template ships — see spec §2.2).
 *                   unknown type -> `undefined`.
 *   - `payload`:    the wire event, verbatim, untouched.
 *
 * Plain-text only: every wire string (`title`, `description`, `type`,
 * payload fields) is carried through as data, never interpreted as
 * HTML/markdown. This module performs no HTML-escaping or markdown
 * rendering — and must never gain any — because the strings it handles are
 * user/producer-controlled and every consumer (toast, feed row) renders
 * them as plain text.
 */
import { getNotificationDisplay } from '@gw/ui';
import type { NotificationContext, NotificationRecord, NotificationSeverity } from '@gw/ui';

/**
 * The decoded wire event shape this adapter consumes. Phase-1 scaffolding
 * per the GW-8880 contract (server-minted `id`) — `@gw-notifications/appsync-events-client`
 * v0.1.0 (the version gw-spa currently depends on) does not yet model `id`
 * on its own event types, and its budget_warning shape still carries the
 * pre-GW-8832-fix `missionName`/`percentSpent` fields. This adapter's own
 * input contract intentionally does NOT import the client's types for that
 * reason; Task 6 (connectionManager, gated on GW-8830/GW-8880) reconciles
 * the real client's decoded event against this shape once v0.2.0 ships.
 */
export interface WireNotificationEvent {
  /** Server-minted, stable id (GW-8880) — the sole source for `NotificationRecord.id`. */
  id: string;
  /** The wire type discriminator, e.g. "mission.run.done". An open string — unknown types must adapt cleanly. */
  type: string;
  /** ISO 8601, appended by the gw-notifications dispatcher from the EventBridge envelope. */
  emittedAt: string;
  /** Every other envelope/payload field (userId, orgId, missionId, runId, title, severity, ...). */
  [key: string]: unknown;
}

const KNOWN_SEVERITIES: ReadonlySet<string> = new Set<NotificationSeverity>([
  'success',
  'error',
  'warning',
  'info',
]);

function isNotificationSeverity(value: unknown): value is NotificationSeverity {
  return typeof value === 'string' && KNOWN_SEVERITIES.has(value);
}

function stringField(wireEvent: WireNotificationEvent, key: string): string | undefined {
  const value = wireEvent[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * Context mapping for the shipped `mission.run.*` family (FR-3's
 * `'mission-run'` kind). Returns `undefined` (never throws) when the wire
 * event is missing either id field — a malformed known-type event still
 * renders in the feed, just ungrouped and unlinked, per spec §3.1.
 */
function missionRunContext(wireEvent: WireNotificationEvent): NotificationContext | undefined {
  const missionId = stringField(wireEvent, 'missionId');
  const runId = stringField(wireEvent, 'runId');
  if (!missionId || !runId) {
    return undefined;
  }
  return { kind: 'mission-run', missionId, runId };
}

/**
 * Maps a known wire `type` to its `NotificationContext`. Only the shipped
 * `mission.run.*` family exists today (spec §2.1) — the other four FR-3
 * kinds (`token-transaction`, `app-session`, `dataset-query`, `rfi`) have no
 * shipped wire contract yet (spec §2.2, "anticipated families"), so there is
 * no wire shape to map from without inventing one. Add a branch here, keyed
 * off that family's real wire `type` prefix, in the same change that adds
 * its `getNotificationDisplay` table entry.
 */
function contextForKnownType(
  type: string,
  wireEvent: WireNotificationEvent
): NotificationContext | undefined {
  if (type.startsWith('mission.run.')) {
    return missionRunContext(wireEvent);
  }
  return undefined;
}

/**
 * Adapts one decoded wire event into a `NotificationRecord` (spec §3.3).
 * Pure — never throws, never mutates `wireEvent`. The caller (the
 * connection manager's `onEvent` handler, GW-8886) feeds the result to
 * `store.ingest()` and `getNotificationToastSpec()`.
 */
export function toNotificationRecord(wireEvent: WireNotificationEvent): NotificationRecord {
  const known = getNotificationDisplay(wireEvent.type, wireEvent);

  if (known) {
    return {
      id: wireEvent.id,
      type: wireEvent.type,
      emittedAt: wireEvent.emittedAt,
      read: false,
      severity: known.severity,
      title: known.title,
      description: known.description,
      context: contextForKnownType(wireEvent.type, wireEvent),
      payload: wireEvent,
    };
  }

  const wireTitle = stringField(wireEvent, 'title');
  const wireSeverity = isNotificationSeverity(wireEvent.severity) ? wireEvent.severity : undefined;

  if (wireTitle && wireSeverity) {
    return {
      id: wireEvent.id,
      type: wireEvent.type,
      emittedAt: wireEvent.emittedAt,
      read: false,
      severity: wireSeverity,
      title: wireTitle,
      context: undefined,
      payload: wireEvent,
    };
  }

  return {
    id: wireEvent.id,
    type: wireEvent.type,
    emittedAt: wireEvent.emittedAt,
    read: false,
    severity: 'info',
    title: wireEvent.type,
    context: undefined,
    payload: wireEvent,
  };
}
